import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { DateTime } from 'luxon';
import clsx from 'clsx';
import DotsIcon from '@app/assets/icons/menu/dot-vertical.svg?react';
import EditIcon from '@app/assets/icons/edit-blue.svg?react';
import DuplicateIcon from '@app/assets/icons/duplicate.svg?react';
import MailIcon from '@app/assets/icons/mail-03.svg?react';
import StarIcon from '@app/assets/icons/star.svg?react';
import BinIcon from '@app/assets/icons/bin.svg?react';
import { MessageTemplateListItem } from '@app/models/messageTemplates';
import { MessageTemplateChannel } from '@app/utils/consts';

const menuItemClassName = 'flex items-center gap-2 p-2 rounded-lg data-[focus]:bg-brand-lightBlue4 text-xs w-full text-left disabled:opacity-40 disabled:cursor-not-allowed';

const formatUsage = ({ automations, campaigns }: MessageTemplateListItem['usageCount'], isDefault?: boolean) => {
  if (!automations && !campaigns) {
    return isDefault ? 'Used as default' : 'Not used';
  }

  return <>
    {automations > 0 && (<div className="my-1">{`${automations} automation${automations > 1 ? 's' : ''}`}</div>)}
    {campaigns > 0 && (<div className="my-1">{`${campaigns} campaign${campaigns > 1 ? 's' : ''}`}</div>)}
  </>;
};

export const MessageTemplatesTableRow = ({
  data,
  canEdit,
  onOpen,
  onDuplicate,
  onSendTest,
  isSendingTest,
  onSetDefault,
  onDelete,
}: {
  data: MessageTemplateListItem;
  canEdit: boolean;
  onOpen: (template: MessageTemplateListItem) => void;
  onDuplicate: (template: MessageTemplateListItem) => void;
  onSendTest: (template: MessageTemplateListItem) => void;
  isSendingTest: boolean;
  onSetDefault: (template: MessageTemplateListItem) => void;
  onDelete: (template: MessageTemplateListItem) => void;
}) => {
  const summary = data.channel === MessageTemplateChannel.EMAIL ? data.subject : data.message;

  return (
    <tr
      className={clsx('border-b border-base-antiFlash last-of-type:border-b-0 text-xs transition-all duration-300', canEdit && 'cursor-pointer hover:bg-brand-lightBlue4')}
      onClick={canEdit ? () => onOpen(data) : undefined}
    >
      <td className="p-4 max-w-[420px]">
        <div className="flex items-center gap-2">
          <p className="font-bold truncate">{data.name}</p>
          {data.isDefault && <span className="bg-brand-baseBlue text-white text-xs px-2 py-1 rounded-full font-medium">Default</span>}
        </div>
        {summary && <p className="text-base-coolGray truncate">{summary}</p>}
      </td>
      <td className="p-4">{DateTime.fromISO(data.updatedAt).toFormat('MMM dd, yyyy')}</td>
      <td className="p-4">{formatUsage(data.usageCount, data.isDefault)}</td>
      <td className="p-4 text-end">
        {canEdit && (
          <Menu>
            <MenuButton className="inline-flex items-center justify-center p-2 bg-[#F6F7FA] rounded" onClick={(e) => e.stopPropagation()}>
              <DotsIcon />
            </MenuButton>
            <MenuItems
              transition
              anchor="left start"
              className="p-2 bg-white shadow-lg mt-2 min-w-[200px] transition duration-200 ease-out data-[closed]:scale-95 data-[closed]:opacity-0 rounded-lg border border-base-antiFlash"
            >
              <MenuItem>
                <button className={menuItemClassName} onClick={(e) => { e.stopPropagation(); onOpen(data); }}>
                  <EditIcon className="[&_path]:stroke-brand-darkBlue3" /> Edit
                </button>
              </MenuItem>
              <MenuItem>
                <button className={menuItemClassName} onClick={(e) => { e.stopPropagation(); onDuplicate(data); }}>
                  <DuplicateIcon className="w-4 h-4 [&_path]:stroke-brand-darkBlue3" /> Duplicate
                </button>
              </MenuItem>
              {data.channel === MessageTemplateChannel.EMAIL && (
                <MenuItem disabled={isSendingTest}>
                  <button className={menuItemClassName} disabled={isSendingTest} onClick={(e) => { e.stopPropagation(); onSendTest(data); }}>
                    <MailIcon className="w-4 h-4 [&_path]:stroke-brand-darkBlue3" /> Send test email
                  </button>
                </MenuItem>
              )}
              {!data.isDefault && (
                <>
                  <MenuItem>
                    <button className={menuItemClassName} onClick={(e) => { e.stopPropagation(); onSetDefault(data); }}>
                      <StarIcon className="w-4 h-4 [&_path]:stroke-brand-darkBlue3" /> Make default
                    </button>
                  </MenuItem>
                  <MenuItem>
                    <button className={menuItemClassName} onClick={(e) => { e.stopPropagation(); onDelete(data); }}>
                      <BinIcon className="[&_path]:stroke-system-error" /> Delete
                    </button>
                  </MenuItem>
                </>
              )}
            </MenuItems>
          </Menu>
        )}
      </td>
    </tr>
  );
};
